import axiosInstance from '../api/axiosInstance';

const CONFIGURATION_META_KEYS = new Set(['configuration_id', 'fk_company_id']);

export const DEFAULT_PIN_COLORS = Object.freeze({
  inactive: '#1890FF',
  active: '#52C41A',
  onDrive: '#FF4D4F',
  pause: '#FFA500',
  busy: '#FF0000',
});

export const CONFIGURATION_GROUPS = Object.freeze([
  { key: 'general', title: 'Opšte' },
  { key: 'colors', title: 'Boje' },
  { key: 'other', title: 'Ostalo' },
]);

export const CONFIGURATION_PARAMETERS = [
  {
    parameter: 'orderMaxRangeInKm',
    description: 'Maksimalni domet narudžbe (km)',
    type: 'number',
    group: 'general',
  },
  {
    parameter: 'prioritizeStandRangeInKm',
    description: 'Prioritet štanda u krugu (km)',
    type: 'number',
    group: 'general',
  },
  {
    parameter: 'standRadiusSizeInMeters',
    description: 'Radijus štanda (metri)',
    type: 'number',
    group: 'general',
  },
  {
    parameter: 'rangeToUserToStartDriveInMeters',
    description: 'Udaljenost do korisnika za početak vožnje (metri)',
    type: 'number',
    group: 'general',
  },
  {
    parameter: 'adPauseInSeconds',
    description: 'Pauza između dvije reklame (sekunde)',
    type: 'number',
    group: 'general',
  },
  {
    parameter: 'pinColorInactive',
    description: 'Boja pina za neaktivne vozače',
    type: 'color',
    group: 'colors',
  },
  {
    parameter: 'pinColorActive',
    description: 'Boja pina za aktivne vozače',
    type: 'color',
    group: 'colors',
  },
  {
    parameter: 'pinColorActiveWithDrive',
    description: 'Boja pina za vozače sa aktivnom vožnjom',
    type: 'color',
    group: 'colors',
  },
  {
    parameter: 'pinColorPause',
    description: 'Boja pina za vozače na pauzi',
    type: 'color',
    group: 'colors',
  },
  {
    parameter: 'pinColorBusy',
    description: 'Boja pina za zauzete vozače',
    type: 'color',
    group: 'colors',
  },
];

const PARAMETER_META = new Map(
  CONFIGURATION_PARAMETERS.map((item) => [item.parameter, item])
);

const PARAMETER_ORDER = new Map(
  CONFIGURATION_PARAMETERS.map((item, index) => [item.parameter, index])
);

export const isConfigurationParameterKey = (key) => !CONFIGURATION_META_KEYS.has(key);

export const getParameterType = (parameter) =>
  PARAMETER_META.get(parameter)?.type ??
  (/^is/i.test(parameter ?? '') ? 'boolean' : 'number');

export const isToggleParameter = (parameter) => getParameterType(parameter) === 'boolean';

export const isColorParameter = (parameter) => getParameterType(parameter) === 'color';

export const normalizeColorValue = (value) => {
  if (value == null || value === '') return null;

  let raw = String(value).trim();
  if (!raw) return null;

  if (!raw.startsWith('#')) {
    raw = `#${raw}`;
  }

  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) {
    return raw.toUpperCase();
  }

  if (/^#[0-9A-Fa-f]{3}$/.test(raw)) {
    const [, r, g, b] = raw;
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }

  return null;
};

export const normalizeConfigurationValue = (value, parameter) => {
  if (isToggleParameter(parameter)) {
    if (value === true || value === 1) {
      return true;
    }
    if (value === false || value === 0) {
      return false;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1';
    }
    return Boolean(value);
  }

  if (isColorParameter(parameter)) {
    return normalizeColorValue(value);
  }

  return value == null || value === '' ? value : Number(value);
};

export const isConfigurationValueChanged = (config) => {
  if (isToggleParameter(config.parameter) || isColorParameter(config.parameter)) {
    return config.value !== config.initialValue;
  }

  return Number(config.value) !== Number(config.initialValue);
};

export const mapConfigurationResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const knownParameters = CONFIGURATION_PARAMETERS.map(
    ({ parameter, description, type, group }) => {
      const value = normalizeConfigurationValue(data[parameter], parameter);

      return {
        parameter,
        description,
        type,
        group,
        value,
        initialValue: value,
      };
    }
  );

  const extraParameters = Object.keys(data)
    .filter(isConfigurationParameterKey)
    .filter((parameter) => !PARAMETER_ORDER.has(parameter))
    .sort()
    .map((parameter) => {
      const type = getParameterType(parameter);
      const value = normalizeConfigurationValue(data[parameter], parameter);

      return {
        parameter,
        description: parameter,
        type,
        group: 'other',
        value,
        initialValue: value,
      };
    });

  return [...knownParameters, ...extraParameters];
};

/** Group mapped config rows into titled sections for the settings UI. */
export const groupConfigurationRows = (configurations) => {
  const byGroup = new Map();

  (configurations ?? []).forEach((config) => {
    const groupKey = config.group ?? 'other';
    if (!byGroup.has(groupKey)) {
      byGroup.set(groupKey, []);
    }
    byGroup.get(groupKey).push(config);
  });

  return CONFIGURATION_GROUPS.map((group) => ({
    ...group,
    items: byGroup.get(group.key) ?? [],
  })).filter((group) => group.items.length > 0);
};

/** Resolve map pin colors from a /configuration response. */
export const readPinColorsFromConfiguration = (data) => ({
  inactive:
    normalizeColorValue(data?.pinColorInactive) ?? DEFAULT_PIN_COLORS.inactive,
  active: normalizeColorValue(data?.pinColorActive) ?? DEFAULT_PIN_COLORS.active,
  onDrive:
    normalizeColorValue(data?.pinColorActiveWithDrive) ?? DEFAULT_PIN_COLORS.onDrive,
  pause: normalizeColorValue(data?.pinColorPause) ?? DEFAULT_PIN_COLORS.pause,
  busy: normalizeColorValue(data?.pinColorBusy) ?? DEFAULT_PIN_COLORS.busy,
});

export const loadConfiguration = async () => {
  const response = await axiosInstance.get('/configuration');
  return response.data;
};

export const saveConfigurationOverrides = (changes) =>
  axiosInstance.put('/configuration', changes);

export const resetConfigurationOverrides = () =>
  axiosInstance.delete('/configuration');
